import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import LightningLandersBuilder from "features/lightningLanders/components/LightningLandersBuilder";
import { useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { saveComponent, readComponent } from "features/campaigns";
import { friendlyDateTime } from "Helpers/dates";
import LightningLanderProvider, {
  useLightningLanderContext,
} from "features/lightningLanders/providers/LightningLanderProvider";
import { ApiComponentType, ApiProjectSettingKey, ApiSimpleComponent } from "@incendium/api";
import {
  landerDefaultTheme,
  LightningLanderThemeProvider,
  useLightningLanderThemeSettings,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { useLanderTemplates } from "features/lightningLanders/hooks/useLanderTemplates";
import { isEmpty } from "Helpers/isEmpty";

function LightningTemplatePageInner() {
  const { componentId } = useParams<{ componentId: string }>();
  const { selectedProject, projectSettings } = useSelectedProject();
  const { setTemplates } = useLanderTemplates();
  const history = useHistory();
  const location = useLocation();
  const { component, setComponent } = useLightningLanderContext();
  const { setTheme: setCustomTheme } = useLightningLanderThemeSettings();
  const creationStarted = useRef(false);

  const onEditSaved = useCallback(
    (component) => {
      setComponent(component);
      setTemplates((components) =>
        produce(components, (draft) => {
          if (!component) {
            return;
          }
          const idx = draft.findIndex((d) => d.id === component?.id);
          if (idx >= 0) {
            draft[idx] = component;
          }
        })
      );
    },
    [setTemplates, setComponent]
  );

  useEffect(() => {
    if (!selectedProject?.id || Boolean(component.id)) {
      return;
    }
    // Prevent multiple creations for /new
    if (componentId === "new" && creationStarted.current) {
      return;
    }

    if (isEmpty(projectSettings)) {
      return;
    }

    if (componentId === "new") {
      creationStarted.current = true;
      const createLanderComponent = async () => {
        const res = await saveComponent(selectedProject.id!, {
          name: `Landing Template (${friendlyDateTime(new Date())})`,
          type: ApiComponentType.LANDING_TEMPLATE,
          theme: projectSettings[ApiProjectSettingKey.LANDING_THEME] || JSON.stringify(landerDefaultTheme)
        });
        setTemplates((components) =>
          produce(components, (draft) => {
            draft.push(res.component!);
          })
        );
        const newPath = location.pathname.replace(
          /\/new$/,
          `/${res.component?.id}`
        );
        history.push(newPath);
      };


      createLanderComponent();
    } else {
      const readLander = async () => {
        const cRes = await readComponent(
          selectedProject?.id as number,
          Number(componentId)
        );

        if (cRes.component?.theme) {
          setCustomTheme(JSON.parse(cRes.component?.theme || "{}")); // Set default theme if not present
        }
        setComponent(cRes.component as ApiSimpleComponent);
      };

      readLander();
    }
  }, [
    selectedProject,
    history,
    location,
    componentId,
    setComponent,
    setTemplates,
    component.id,
    setCustomTheme,
    projectSettings
  ]);

  if (!selectedProject || !component?.id) {
    return (
      <CenterPage calcHeight>
        <Loading />
      </CenterPage>
    );
  }

  return (
    <LightningLandersBuilder
      saveToken={null}
      project={selectedProject}
      onSaved={onEditSaved}
    />
  );
}

function LightningTemplatePage() {
  return (
    <LightningLanderProvider>
      <LightningLanderThemeProvider>
        <LightningTemplatePageInner />
      </LightningLanderThemeProvider>
    </LightningLanderProvider>
  );
}

export default LightningTemplatePage;
